import React, { useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import './nx.css';

/**
 * Renders a block of Imfuyo page content as normal React elements and wires its links:
 *  data-go="page" [data-section="x"]  -> onNavigate(page, section)
 *  data-scroll="id"                   -> smooth scroll to #id on this page
 *  data-action="register"             -> onRegister()
 *  data-yt="VIDEO_ID"                 -> thumbnail that turns into the YouTube player
 *  data-slot="name"                   -> a live React piece passed in through `slots`
 */
const VideoButton = ({ id, style, label }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <button type="button" className="nx-video" style={style} aria-label={label || 'Play video'}
            onClick={() => setPlaying(true)}>
      {playing ? (
        <iframe title="Imfuyo video" allowFullScreen
                src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen" />
      ) : (
        <>
          <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt="" />
          <span className="nx-play" aria-hidden="true" />
        </>
      )}
    </button>
  );
};

const toStyle = (s) => {
  const o = {};
  (s || '').split(';').forEach((d) => {
    const i = d.indexOf(':');
    if (i < 0) return;
    const k = d.slice(0, i).trim();
    const v = d.slice(i + 1).trim();
    if (k) o[k.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = v;
  });
  return o;
};

const NxPage = ({ html, onNavigate, onRegister, scrollTop = true, slots }) => {
  const ref = useRef(null);

  useEffect(() => { if (scrollTop) window.scrollTo(0, 0); }, [html, scrollTop]);

  const onClick = (e) => {
    const t = e.target.closest('[data-go],[data-scroll],[data-action]');
    if (!t || !ref.current || !ref.current.contains(t)) return;
    e.preventDefault();
    if (t.dataset.action === 'register') { onRegister && onRegister(); return; }
    if (t.dataset.scroll) {
      const el = ref.current.querySelector('#' + t.dataset.scroll);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (t.dataset.go && onNavigate) onNavigate(t.dataset.go, t.dataset.section || undefined);
  };

  const content = parse(html, {
    replace: (node) => {
      const a = node.attribs;
      if (!a) return undefined;
      if (a['data-slot'] && slots && slots[a['data-slot']]) {
        return <div style={toStyle(a.style)}>{slots[a['data-slot']]}</div>;
      }
      if (node.name === 'button' && a['data-yt']) {
        return <VideoButton id={a['data-yt']} style={toStyle(a.style)} label={a['aria-label']} />;
      }
      return undefined;
    },
  });

  return <div className="nx" ref={ref} onClick={onClick}>{content}</div>;
};

export default NxPage;
